"use client";

import { useState, useEffect, ChangeEvent, FormEvent, useCallback } from "react";
import Button from "../../components/ui/button/Button";
import { Modal } from "../../components/ui/modal";
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../components/ui/table";
import Badge from "../../components/ui/badge/Badge";
import ComponentCard from "../../components/common/ComponentCard";
import type { ReservaB2C, ClienteB2C, Excursion, NoShow, ReservationStatus } from "../../hooks/useApi";
import useApi from "../../hooks/useApi";
import { useNavigate } from "react-router";
import CreateClientModal from "../../components/clients/CreateClientModal";
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';
import Input from "../../components/form/input/InputField";
import useAuth from "../../hooks/useAuth";
import RoleBasedRenderer from "../../components/auth/RoleBasedRenderer";
import { Calendar, DollarSign, FileText, User, Users, AlertCircle, ClipboardList, CreditCard, TrendingUp, Tag, Info, FileCheck, Building2, Eye, Edit, Trash2, Search } from 'lucide-react';
import ReservaDetailsModal from "../../components/reservas/ReservaDetailsModal";
import DuplicateReservationCheck from "../../components/reservas/DuplicateReservationCheck";

// Helper: Convierte "dd/mm/yyyy" a "yyyy-mm-dd" para compatibilidad con DatePicker
function formatDateForInput(dateStr: string): string {
  if (!dateStr) return "";
  
  // If already in ISO format, return as is
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}(T|$)/)) {
    return dateStr.split('T')[0];
  }
  
  // Handle dd/mm/yyyy format
  const parts = dateStr.split("/");
  if (parts.length !== 3) return "";
  const [day, month, year] = parts;
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}

type BadgeColor = "primary" | "success" | "error" | "warning" | "info" | "light" | "dark";

interface ReservaFormData {
  cliente: string;
  excursion: string;
  fecha: string;
  adultos: string;
  ninios: string;
  hotel: string;
  metodoPago: "efectivo" | "tarjeta" | "transferencia";
  notas: string;
  descuento: string;
  montoFacturado: string;
  estado: string;
  taxRate: string;
  incluyeSeguro: boolean;
}

interface B2CReservasProps {
  reservas: ReservaB2C[];
  searchTerm: string;
  dateFilter: string;
  thirdPartyFilter: boolean | null;
  onSearchChange: (value: string) => void;
  onDateFilterChange: (value: string) => void;
  onThirdPartyFilterChange: (value: boolean | null) => void;
  onAddReserva: (newReserva: Omit<ReservaB2C, "id">) => Promise<void>;
  onEditReserva: (updatedReserva: ReservaB2C) => void;
  onDeleteReserva: (id: number) => void;
}

export default function B2CReservas({
  reservas,
  searchTerm,
  dateFilter,
  thirdPartyFilter,
  onSearchChange,
  onDateFilterChange,
  onThirdPartyFilterChange,
  onAddReserva,
  onEditReserva,
  onDeleteReserva,
}: B2CReservasProps) {
  const { getClients, getExcursiones, getHotels, calculatePrice, registerPayment, createClient, registerNoShow, getReservationNoShows, deleteNoShow, getReservas } = useApi();
  const navigate = useNavigate();
  const { canCreate, canEdit, canDelete } = useAuth();

  // Create stable references to API functions
  const stableGetClients = useCallback(getClients, []);
  const stableGetExcursiones = useCallback(getExcursiones, []);
  const stableGetHotels = useCallback(getHotels, []);

  const estadoDisplay = {
    pending_payment: "Pendiente",
    partial_payment: "Pago parcial",
    paid: "Pagada",
    no_show: "No Show",
    completed: "Completada"
  };

  // Modal para creación/edición
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingReserva, setEditingReserva] = useState<ReservaB2C | null>(null);
  // En creación: el estado no se envía (se asigna automáticamente en backend)
  // En edición: se permite modificar manualmente el estado, pero solo entre "no_show" y "completed"
  const [reservaFormData, setReservaFormData] = useState<ReservaFormData>({
    cliente: "",
    excursion: "",
    fecha: "",
    adultos: "1",
    ninios: "0",
    hotel: "",
    metodoPago: "efectivo",
    notas: "",
    descuento: "0",
    montoFacturado: "0",
    estado: "pending_payment",
    taxRate: "0",
    incluyeSeguro: false
  });

  const [clientes, setClientes] = useState<ClienteB2C[]>([]);
  const [excursiones, setExcursiones] = useState<Excursion[]>([]);
  const [hotels, setHotels] = useState<string[]>([]);

  // Modal de detalles
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedReserva, setSelectedReserva] = useState<ReservaB2C | null>(null);

  // Modal de confirmación para eliminación
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [reservationIdToDelete, setReservationIdToDelete] = useState<number | null>(null);

  // Modal para registrar pago adicional
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedReservaForPayment, setSelectedReservaForPayment] = useState<ReservaB2C | null>(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<"efectivo" | "tarjeta" | "transferencia">("efectivo");
  const [justificacionExtra, setJustificacionExtra] = useState("");

  // Modal para crear nuevo cliente
  const [isNewClientModalOpen, setIsNewClientModalOpen] = useState(false);

  const today = new Date().toISOString().split("T")[0];

  const [adultoPrice, setAdultoPrice] = useState<number | null>(null);
  const [ninioPrice, setNinioPrice] = useState<number | null>(null);
  const [calculatedPricePreview, setCalculatedPricePreview] = useState<number | null>(null);
  const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
  const [hasCustomPrice, setHasCustomPrice] = useState(false);
  const [justificacionSobrepago, setJustificacionSobrepago] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Add pagination state variables after the other state declarations
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // Number of reservations to show per page

  // Add a useEffect to reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateFilter, thirdPartyFilter]);

  // Al editar, precargar datos (cliente, monto facturado y estado)
  useEffect(() => {
    if (editingReserva && clientes.length > 0) {
      const clientId = editingReserva.cliente ?? editingReserva.clientId;
      setReservaFormData((prev) => ({
        ...prev,
        cliente: clientId ? clientId.toString() : "",
        montoFacturado: editingReserva.montoFacturado.toString(),
        estado: editingReserva.estado, // En edición se permite cambiar manualmente el estado
      }));
    }
  }, [editingReserva, clientes]);

  useEffect(() => {
    async function fetchDropdownData() {
      try {
        const clientsData = await stableGetClients();
        setClientes(clientsData);
      } catch (error) {
        toast.error("Error obteniendo clientes: " + (error instanceof Error ? error.message : "Error desconocido"));
      }
      try {
        const excursionsData = await stableGetExcursiones();
        setExcursiones(excursionsData);
      } catch (error) {
        toast.error("Error obteniendo excursiones: " + (error instanceof Error ? error.message : "Error desconocido"));
      }
    }
    fetchDropdownData();
  }, [stableGetClients, stableGetExcursiones]);

  useEffect(() => {
    async function fetchHotels() {
      try {
        const hotelsData = await stableGetHotels();
        setHotels(hotelsData);
      } catch (error) {
        toast.error("Error obteniendo hoteles: " + (error instanceof Error ? error.message : "Error desconocido"));
      }
    }
    fetchHotels();
  }, [stableGetHotels]);

  const [noShows, setNoShows] = useState<NoShow[]>([]);
  const [userRole, setUserRole] = useState<string>("worker");

  // Get user role from localStorage when component mounts
  useEffect(() => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(window.atob(base64));
        
        if (payload.role) {
          setUserRole(payload.role);
        }
      }
    } catch (error) {
      console.error('Error getting user role:', error);
    }
  }, []);

  const handleOpenAddModal = () => {
    setReservaFormData({
      cliente: "",
      excursion: "",
      fecha: "",
      adultos: "1",
      ninios: "0",
      hotel: "",
      metodoPago: "efectivo",
      notas: "",
      descuento: "0",
      montoFacturado: "0",
      estado: "pending_payment",
      taxRate: "0",
      incluyeSeguro: false
    });
    setCalculatedPricePreview(null);
    setEditingReserva(null);
    setIsFormModalOpen(true);
  };

  const handleOpenEditModal = (reserva: ReservaB2C) => {
    setReservaFormData({
      cliente: reserva.cliente.toString(),
      excursion: reserva.excursion,
      fecha: formatDateForInput(reserva.fechaISO || reserva.fecha),
      adultos: reserva.adultos.toString(),
      ninios: reserva.ninios.toString(),
      hotel: reserva.hotel,
      metodoPago: reserva.metodoPago || "efectivo",
      notas: reserva.notas || "",
      descuento: reserva.descuento?.toString() || "0",
      montoFacturado: reserva.montoFacturado?.toString() || "0",
      estado: reserva.estado,
      taxRate: reserva.taxRate?.toString() || "0",
      incluyeSeguro: reserva.incluyeSeguro ?? false
    });
    setEditingReserva(reserva);
    setIsFormModalOpen(true);
  };

  const handleOpenDetailModal = async (reserva: ReservaB2C) => {
    setSelectedReserva(reserva);
    setIsDetailModalOpen(true);
    
    // Fetch no shows for this reservation
    try {
      const fetchedNoShows = await getReservationNoShows(reserva.id);
      setNoShows(fetchedNoShows);
    } catch (error) {
      console.error("Error fetching no shows:", error);
      toast.error("Error al cargar los registros de No Show");
    }
  };

  // Para registrar pago adicional si la reserva tiene saldo pendiente y no está en "no_show"
  const handleOpenPaymentModal = (reserva: ReservaB2C) => {
    if (reserva.estado === "no_show" || reserva.saldoPendiente <= 0) return;
    setSelectedReservaForPayment(reserva);
    setPaymentAmount("");
    setPaymentMethod("efectivo");
    setJustificacionExtra(""); // Reset justification when opening modal
    setIsPaymentModalOpen(true);
  };

  // Calculate price preview in real-time when form values change
  const calculatePricePreview = async () => {
    if (
      !reservaFormData.excursion ||
      !reservaFormData.adultos ||
      (parseInt(reservaFormData.adultos) === 0 && (!reservaFormData.ninios || parseInt(reservaFormData.ninios) === 0))
    ) {
      setCalculatedPricePreview(null);
      setAdultoPrice(null);
      setNinioPrice(null);
      return;
    }

    try {
      setIsCalculatingPrice(true);
      
      const adultos = parseInt(reservaFormData.adultos) || 0;
      const ninios = parseInt(reservaFormData.ninios) || 0;
      const descuento = parseInt(reservaFormData.descuento) || 0;
      const taxRate = parseInt(reservaFormData.taxRate) || 0;

      const response = await calculatePrice({
        type: "b2c",
        excursion: reservaFormData.excursion,
        adultos,
        ninios,
        descuento,
        taxRate,
        incluyeSeguro: reservaFormData.incluyeSeguro
      });

      if (typeof response === 'object' && 'price' in response) {
        setCalculatedPricePreview(response.price);
        setAdultoPrice(response.details?.adultoPrice || 0);
        setNinioPrice(response.details?.ninioPrice || 0);
        setHasCustomPrice(response.details?.hasCustomPrice || false);
      } else {
        setCalculatedPricePreview(response as number);
        setAdultoPrice(null);
        setNinioPrice(null);
        setHasCustomPrice(false);
      }
    } catch (error) {
      console.error("Error al calcular precio:", error);
      setCalculatedPricePreview(null);
      setAdultoPrice(null);
      setNinioPrice(null);
      setHasCustomPrice(false);
    } finally {
      setIsCalculatingPrice(false);
    }
  };

  // Trigger price calculation when form values change that affect price
  useEffect(() => {
    if (isFormModalOpen) {
      const debouncedCalculation = setTimeout(() => {
        calculatePricePreview();
      }, 500); // Debounce to avoid too many API calls
      
      return () => clearTimeout(debouncedCalculation);
    }
  }, [
    isFormModalOpen,
    reservaFormData.excursion,
    reservaFormData.adultos, 
    reservaFormData.ninios,
    reservaFormData.descuento,
    reservaFormData.taxRate,
    reservaFormData.incluyeSeguro,
    calculatePricePreview
  ]);

  // Reset justification when form data changes
  useEffect(() => {
    // Check if we need to clear the justification (when montoFacturado is no longer > calculatedPricePreview)
    const montoFacturado = parseFloat(reservaFormData.montoFacturado || "0");
    if (calculatedPricePreview === null || montoFacturado <= calculatedPricePreview) {
      setJustificacionSobrepago("");
    }
  }, [reservaFormData.montoFacturado, calculatedPricePreview]);

  const handleSaveReserva = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validaciones del formulario
      if (
        !reservaFormData.cliente ||
        !reservaFormData.excursion ||
        !reservaFormData.fecha ||
        !reservaFormData.adultos ||
        !reservaFormData.hotel
      ) {
        toast.error("Por favor complete todos los campos requeridos");
        setIsSubmitting(false);
        return;
      }

      // Validaciones básicas
      if (!reservaFormData.cliente.trim()) {
        toast.error("Debe seleccionar un cliente");
        setIsSubmitting(false);
        return;
      }
      if (!reservaFormData.excursion.trim()) {
        toast.error("Debe seleccionar una excursión");
        setIsSubmitting(false);
        return;
      }
      if (!reservaFormData.fecha.trim()) {
        toast.error("Debe seleccionar una fecha");
        setIsSubmitting(false);
        return;
      }
      
      // Ensure date is in the correct format (yyyy-MM-dd)
      let formattedDate = reservaFormData.fecha;
      if (formattedDate && typeof formattedDate === 'string') {
        // If date already contains time part, remove it
        if (formattedDate.includes('T')) {
          formattedDate = formattedDate.split('T')[0];
        }
        // Validate the date format
        if (!formattedDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
          toast.error("Formato de fecha inválido. Use yyyy-MM-dd");
          setIsSubmitting(false);
          return;
        }
      }
      
      if (!reservaFormData.adultos || Number(reservaFormData.adultos) < 1) {
        toast.error("La cantidad de adultos debe ser al menos 1");
        setIsSubmitting(false);
        return;
      }

      const totalAdultos = Number(reservaFormData.adultos);
      const totalNinios = reservaFormData.ninios ? Number(reservaFormData.ninios) : 0;
      const totalPersonas = totalAdultos + totalNinios;
      const discountValue = Number(reservaFormData.descuento) || 0;
      const initialPayment = Number(reservaFormData.montoFacturado) || 0;

      // Si estamos editando y no se modificaron los valores críticos, mantener el precio original
      // BUT we still want to update taxRate if it changed
      if (editingReserva && 
          totalAdultos === editingReserva.adultos && 
          totalNinios === editingReserva.ninios && 
          reservaFormData.excursion === editingReserva.excursion && 
          discountValue === editingReserva.descuento) {
        
        // Check if taxRate has changed
        const currentTaxRate = Number(reservaFormData.taxRate) || 0;
        const taxRateChanged = editingReserva.taxRate !== currentTaxRate;
        
        if (taxRateChanged) {
          console.log('Tax rate has changed, will recalculate price', {
            oldTaxRate: editingReserva.taxRate,
            newTaxRate: currentTaxRate
          });
          
          // If tax rate changed, we need to recalculate price
          try {
            console.log("Recalculando precio por cambio de impuesto");
            
            const priceResponse = await calculatePrice({
              type: "b2c",
              excursion: reservaFormData.excursion,
              adultos: totalAdultos,
              ninios: totalNinios,
              descuento: discountValue,
              taxRate: currentTaxRate,
              incluyeSeguro: Boolean(reservaFormData.incluyeSeguro)
            });
            
            // Extract price from response
            const calculatedPrice = typeof priceResponse === 'object' && 'price' in priceResponse
              ? priceResponse.price 
              : priceResponse as number;
            
            // Create updated reservation with new tax rate and calculated price
            const updatedReserva: ReservaB2C = {
              ...editingReserva,
              cliente: parseInt(reservaFormData.cliente),
              fecha: formattedDate,
              hotel: reservaFormData.hotel,
              metodoPago: reservaFormData.metodoPago as "efectivo" | "tarjeta" | "transferencia",
              notas: reservaFormData.notas,
              estado: reservaFormData.estado as ReservaB2C["estado"],
              taxRate: currentTaxRate,
              precioTotal: calculatedPrice,
              saldoPendiente: calculatedPrice - editingReserva.montoFacturado
            };
    
            await onEditReserva(updatedReserva);
            setIsFormModalOpen(false);
            toast.success("Reserva actualizada correctamente");
            return;
          } catch (error) {
            console.error("Error recalculando precio por cambio de impuesto:", error);
            toast.error("Error al recalcular el precio con nuevo impuesto");
            return;
          }
        } else {
          // No changes in tax rate or critical fields, do a simple update
          const updatedReserva: ReservaB2C = {
            ...editingReserva,
            cliente: parseInt(reservaFormData.cliente),
            fecha: formattedDate,
            hotel: reservaFormData.hotel,
            metodoPago: reservaFormData.metodoPago as "efectivo" | "tarjeta" | "transferencia",
            notas: reservaFormData.notas,
            estado: reservaFormData.estado as ReservaB2C["estado"]
          };
    
          onEditReserva(updatedReserva);
          setIsFormModalOpen(false);
          toast.success("Reserva actualizada correctamente");
          return;
        }
      }

      // Si es una nueva reserva o se modificaron valores críticos, calcular nuevo precio
      console.log('Calculando precio...', {
        type: "b2c",
        excursion: reservaFormData.excursion,
        adultos: totalAdultos,
        ninios: totalNinios,
        descuento: discountValue,
        taxRate: Number(reservaFormData.taxRate || "0")
      });

      let calculatedPrice: number;
      try {
        // Use the pre-calculated price if available to avoid an extra API call
        if (calculatedPricePreview !== null) {
          calculatedPrice = calculatedPricePreview;
        } else {
          const priceResponse = await calculatePrice({
            type: "b2c",
            excursion: reservaFormData.excursion,
            adultos: parseInt(reservaFormData.adultos),
            ninios: parseInt(reservaFormData.ninios || "0"),
            descuento: parseFloat(reservaFormData.descuento || "0"),
            taxRate: parseFloat(reservaFormData.taxRate || "0"),
            incluyeSeguro: Boolean(reservaFormData.incluyeSeguro)
          });
          
          // Extract price from response
          calculatedPrice = typeof priceResponse === 'object' && 'price' in priceResponse
            ? priceResponse.price 
            : priceResponse as number;
        }
      } catch (error) {
        console.error("Error calculando el precio:", error);
        toast.error(error instanceof Error ? error.message : 'Error al calcular el precio');
        return;
      }

      console.log('Precio calculado:', calculatedPrice);

      // Validate overpayment has justification
      if (initialPayment > calculatedPrice && !justificacionSobrepago.trim()) {
        toast.error("Debe proporcionar una justificación cuando el pago inicial excede el precio total");
        setIsSubmitting(false);
        return;
      }

      // Determinar estado
      const saldoPendiente = initialPayment >= calculatedPrice ? 0 : calculatedPrice - initialPayment;
      const estado: ReservaB2C["estado"] = editingReserva 
        ? reservaFormData.estado as ReservaB2C["estado"]
        : initialPayment === 0 
          ? "pending_payment" 
          : saldoPendiente > 0 
            ? "partial_payment" 
            : "paid";

      if (editingReserva) {
        // Validar que no se pueda marcar como no_show si ya está completada
        if (reservaFormData.estado === "no_show" && editingReserva.estado === "completed") {
          toast.error("No se puede marcar como No-Show una reserva que ya está completada");
          return;
        }

        // Validar que no se pueda marcar como completada si tiene saldo pendiente
        if (reservaFormData.estado === "completed" && editingReserva.saldoPendiente > 0) {
          toast.error("No se puede marcar como completada una reserva con saldo pendiente");
          return;
        }

        try {
          // Actualizar reserva existente con nuevo precio
          const updatedReserva: ReservaB2C = {
            ...editingReserva,
            cliente: parseInt(reservaFormData.cliente),
            excursion: reservaFormData.excursion,
            fecha: formattedDate,
            personas: totalPersonas,
            adultos: totalAdultos,
            ninios: totalNinios,
            hotel: reservaFormData.hotel,
            precioTotal: calculatedPrice,
            metodoPago: reservaFormData.metodoPago as "efectivo" | "tarjeta" | "transferencia",
            notas: reservaFormData.notas,
            descuento: discountValue,
            montoFacturado: initialPayment,
            saldoPendiente,
            estado: reservaFormData.estado as ReservaB2C["estado"] || estado,
            taxRate: Number(reservaFormData.taxRate || "0")
          };

          await onEditReserva(updatedReserva);
          toast.success("Reserva actualizada correctamente");
          setIsFormModalOpen(false);
        } catch (error) {
          if (error instanceof Error) {
            if (error.message.includes("capacidad disponible")) {
              toast.error(error.message);
            } else if (error.message.includes("No-Show")) {
              toast.error(error.message);
            } else if (error.message.includes("saldo pendiente")) {
              toast.error(error.message);
            } else {
              toast.error("Error al actualizar la reserva");
            }
          }
          return;
        }
      } else {
        try {
          // Crear nueva reserva
          const newReserva: Omit<ReservaB2C, "id"> = {
            type: "b2c",
            cliente: parseInt(reservaFormData.cliente),
            excursion: reservaFormData.excursion,
            fecha: formattedDate,
            personas: totalPersonas,
            adultos: totalAdultos,
            ninios: totalNinios,
            hotel: reservaFormData.hotel,
            precioTotal: calculatedPrice,
            metodoPago: reservaFormData.metodoPago,
            notas: reservaFormData.notas,
            montoFacturado: initialPayment,
            saldoPendiente: saldoPendiente,
            estado: estado,
            taxRate: Number(reservaFormData.taxRate),
            // Add manual adjustment properties when needed
            ...(initialPayment > calculatedPrice ? {
              hasManualAdjustment: true,
              adjustmentReason: justificacionSobrepago
            } : {}),
            incluyeSeguro: reservaFormData.incluyeSeguro
          };

          await onAddReserva(newReserva);
          toast.success("Reserva creada correctamente");
          setIsFormModalOpen(false);
        } catch (error) {
          // Manejar específicamente errores de capacidad y otros errores conocidos
          if (error instanceof Error && error.message.includes("capacidad disponible")) {
            toast.error(error.message);
            return;
          }
          // Para otros errores, mantener el comportamiento actual
          throw error;
        }
      }

      // Limpiar el formulario solo si todo fue exitoso
      setReservaFormData({
        cliente: "",
        excursion: "",
        fecha: "",
        adultos: "",
        ninios: "",
        hotel: "",
        metodoPago: "efectivo",
        notas: "",
        descuento: "",
        montoFacturado: "",
        estado: "",
        taxRate: "0",
        incluyeSeguro: false
      });
      setCalculatedPricePreview(null);
      setJustificacionSobrepago(""); // Reset justification

    } catch (error) {
      console.error("Error al guardar la reserva:", error);
      toast.error(
        error instanceof Error 
          ? error.message 
          : "Error al guardar la reserva. Por favor, intente nuevamente"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Función para registrar pagos adicionales
  const handleRegisterPayment = async () => {
    if (!selectedReservaForPayment) return;
    const amount = Number(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Ingrese un monto de pago válido");
      return;
    }
    
    // Check if it's an overpayment
    const isOverpayment = amount > selectedReservaForPayment.saldoPendiente;
    
    // If it's an overpayment and no justification provided, show error
    if (isOverpayment && !justificacionExtra.trim()) {
      toast.error("Para pagos mayores al saldo pendiente, debe proporcionar una justificación");
      return;
    }
    
    try {
      // Include justification if it's an overpayment
      const updatedReserva = await registerPayment(
        selectedReservaForPayment.id, 
        amount, 
        paymentMethod,
        isOverpayment ? justificacionExtra : undefined
      );
      onEditReserva(updatedReserva as ReservaB2C);
      setIsPaymentModalOpen(false);
      toast.success("Pago registrado correctamente");
    } catch (error) {
      console.error("Error registrando el pago:", error);
      toast.error("Error al registrar el pago");
    }
  };

  const filteredReservas = reservas.filter((reserva) => {
    // 1. Client filter - check if the client name contains the search term (not exact match)
    // First normalize values to prevent case/spacing issues
    const normalizedClientName = reserva.clienteName?.toLowerCase().trim() || "";
    const normalizedSearchTerm = searchTerm.toLowerCase().trim();
    const matchesClient = !normalizedSearchTerm || normalizedClientName.includes(normalizedSearchTerm);

    // 2. Date filter - correctly handle both ISO and display date formats
    // First normalize date formats for comparison
    const reservaDate = reserva.fechaISO || formatDateForInput(reserva.fecha);
    const matchesDate = !dateFilter || reservaDate === dateFilter;

    // 3. Third party filter - explicitly check for boolean values
    const isThirdParty = Boolean(reserva.isThirdParty);
    // If thirdPartyFilter is null, show all. Otherwise, match the exact boolean value
    const matchesThirdParty = thirdPartyFilter === null || isThirdParty === thirdPartyFilter;

    // All conditions must be satisfied for the reservation to be included
    return matchesClient && matchesDate && matchesThirdParty;
  });

  // Add pagination controls functions
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Calculate pagination values after filteredReservas is defined
  const totalPages = Math.ceil(filteredReservas.length / itemsPerPage);
  const paginatedReservas = filteredReservas.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Add a function to reset filters
  const resetFilters = () => {
    onSearchChange("");
    onDateFilterChange("");
    onThirdPartyFilterChange(null);
  };

  function getStatusBadgeColor(status: string): BadgeColor {
    switch (status) {
      case "pending_payment":
        return "primary";
      case "partial_payment":
        return "info";
      case "paid":
        return "success";
      case "no_show":
        return "warning";
      case "completed":
        return "dark";
      default:
        return "light";
    }
  }

  // Modificar la función para obtener clientes únicos
  const getUniqueClientsFromReservations = () => {
    const uniqueClients = new Map();
    reservas.forEach((res) => {
      if (res.clienteName) {
        uniqueClients.set(res.clienteName, {
          id: res.cliente,
          name: res.clienteName
        });
      }
    });
    return Array.from(uniqueClients.values())
      .sort((a, b) => a.name.localeCompare(b.name)); // Ordenar alfabéticamente
  };

  const handleExportToExcel = () => {
    // Prepare the data for export - using filtered reservas instead of all reservas
    const exportData = filteredReservas.map(res => ({
      'Cliente': res.clienteName || '',
      'Excursión': res.excursion,
      'Fecha': res.fecha,
      'Adultos': res.adultos,
      'Niños': res.ninios,
      'Total Personas': res.personas,
      'Hotel': res.hotel || '',
      'Método de Pago': res.metodoPago,
      'Precio Total': res.precioTotal,
      'Monto Facturado': res.montoFacturado,
      'Saldo Pendiente': res.saldoPendiente,
      'Estado': estadoDisplay[res.estado],
      'Notas': res.notas || '',
      'Terceros': res.isThirdParty ? 'Terceros' : 'Propia'
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Reservas B2C');

    // Generate file name with current date and filter info
    const date = new Date().toISOString().split('T')[0];
    let fileName = `reservas_b2c_${date}`;
    
    // Add filter information to filename
    if (searchTerm) {
      fileName += `_cliente_${searchTerm.replace(/\s+/g, '_')}`;
    }
    if (dateFilter) {
      fileName += `_fecha_${dateFilter}`;
    }
    if (thirdPartyFilter !== null) {
      fileName += thirdPartyFilter ? '_terceros' : '_propias';
    }
    
    fileName += '.xlsx';

    // Save the file
    XLSX.writeFile(wb, fileName);
    toast.success(`Exportadas ${filteredReservas.length} reservas a Excel`);
  };

  // Function to refresh the list of No Shows for a reservation
  const refreshNoShows = async () => {
    if (selectedReserva) {
      try {
        const fetchedNoShows = await getReservationNoShows(selectedReserva.id);
        setNoShows(fetchedNoShows);
      } catch (error) {
        console.error("Error fetching no shows:", error);
      }
    }
  };

  const handleDeleteNoShow = async (id: number) => {
    try {
      await deleteNoShow(id);
      toast.success("No Show eliminado correctamente");
      
      // Refresh the list of no shows if we have a selected reservation
      if (selectedReserva) {
        await refreshNoShows();
        
        // If this was the only no show and the reservation is marked as no_show,
        // we should update the reservation status
        if (noShows.length === 1 && selectedReserva.estado === "no_show") {
          // Determine the new status based on payment status
          let newStatus: ReservationStatus = "pending_payment";
          
          if (selectedReserva.saldoPendiente <= 0) {
            newStatus = "paid";
          } else if (selectedReserva.montoFacturado > 0) {
            newStatus = "partial_payment";
          }
          
          // Update local state
          const updatedReserva = {
            ...selectedReserva,
            estado: newStatus
          };
          
          setSelectedReserva(updatedReserva);
          
          // Update parent component
          onEditReserva(updatedReserva);
        }
      }
    } catch (error) {
      console.error("Error deleting no show:", error);
      toast.error("Error al eliminar el no show");
      throw error;
    }
  };

  const handleRegisterNoShow = async (data: {
    reservationId: number;
    missingAdults: number;
    missingChildren: number;
    justificacion?: string;
  }) => {
    try {
      const result = await registerNoShow(data);
      
      // If the entire reservation is marked as no-show, we need to refresh the main list
      if (result.reservationUpdated) {
        toast.success("La reserva ha sido marcada como No Show");
        
        // Refresh the selected reservation to show updated status
        if (selectedReserva) {
          try {
            // Refresh no shows
            const fetchedNoShows = await getReservationNoShows(data.reservationId);
            setNoShows(fetchedNoShows);
            
            // Update the local reservation status
            const updatedReserva = {
              ...selectedReserva,
              estado: 'no_show' as const
            };
            setSelectedReserva(updatedReserva);
            
            // Update the reservation in the parent component
            onEditReserva(updatedReserva);
          } catch (refreshError) {
            console.error("Error refreshing reservation data:", refreshError);
          }
        }
      } else {
        toast.success("No Show registrado correctamente");
        
        // Refresh no shows
        const fetchedNoShows = await getReservationNoShows(data.reservationId);
        setNoShows(fetchedNoShows);
      }
      
      return; // Return void to match the expected type
    } catch (error) {
      console.error("Error registering no show:", error);
      toast.error("Error al registrar el no show");
      throw error; // Re-throw to propagate to the modal
    }
  };

  return (
    <div className="space-y-6">
      <ComponentCard className="mb-6" title="Filtros de búsqueda">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Input
                type="text"
                value={searchTerm}
                onChange={(e: ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 focus:border-blue-500 focus:ring-blue-500"
                placeholder="Buscar por nombre de cliente"
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Search className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              </div>
            </div>
            <Input
              type="date"
              value={dateFilter}
              onChange={(e) => onDateFilterChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 focus:border-blue-500 focus:ring-blue-500"
              placeholder="Filtrar por fecha"
              dateFormat="yyyy-MM-dd"
            />
            <select
              value={thirdPartyFilter === null ? "all" : thirdPartyFilter ? "thirdParty" : "own"}
              onChange={(e) => {
                const value = e.target.value;
                const thirdPartyValue = value === "all" ? null : value === "thirdParty";
                onThirdPartyFilterChange(thirdPartyValue);
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="all">Todas las excursiones</option>
              <option value="own">Excursiones propias</option>
              <option value="thirdParty">Excursiones de terceros</option>
            </select>
          <div className="flex gap-2">
            <RoleBasedRenderer requiredPermission="create">
              <Button variant="primary" onClick={handleOpenAddModal} className="flex-1">
                Nueva reserva
              </Button>
            </RoleBasedRenderer>
            <Button variant="outline" onClick={handleExportToExcel} className="flex-1">
              Exportar Excel
            </Button>
          </div>
        </div>
      </ComponentCard>

      <ComponentCard title="Reservas B2C">
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-gray-100 dark:scrollbar-track-gray-800">
          <Table className="w-max divide-y divide-gray-200 dark:divide-gray-700">
            <TableHeader className="bg-gray-50 dark:bg-gray-800">
              <TableRow>
                <TableCell isHeader className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider sm:px-6">
                  Cliente
                </TableCell>
                <TableCell isHeader className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider sm:px-6">
                  Excursión
                </TableCell>
                <TableCell isHeader className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider sm:px-6">
                  Fecha
                </TableCell>
                <TableCell isHeader className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider sm:px-6">
                  Estado de Pago
                </TableCell>
                <TableCell isHeader className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider sm:px-6">
                  Acciones
                </TableCell>
              </TableRow>
            </TableHeader>
            <TableBody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedReservas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    {searchTerm || dateFilter 
                      ? "No se encontraron reservas con los filtros aplicados" 
                      : "No hay reservas registradas"}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedReservas.map((res) => (
                  <TableRow key={res.id}>
                    <TableCell className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 sm:px-6">
                      {res.clienteName}
                    </TableCell>
                    <TableCell className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 sm:px-6">
                      {res.excursion}
                    </TableCell>
                    <TableCell className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 sm:px-6">
                      {res.fecha}
                    </TableCell>
                    <TableCell className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 sm:px-6">
                      <Badge color={getStatusBadgeColor(res.estado)}>
                        {estadoDisplay[res.estado]}
                      </Badge>
                    </TableCell>
                    <TableCell className="px-4 py-4 whitespace-nowrap text-sm font-medium sm:px-6">
                      <div className="flex space-x-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="p-2" 
                          aria-label="Ver detalles"
                          onClick={() => handleOpenDetailModal(res)}>
                          <Eye size={16} />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="p-2" 
                          aria-label="Editar reserva"
                          onClick={() => handleOpenEditModal(res)}>
                          <Edit size={16} />
                        </Button>
                        <RoleBasedRenderer requiredPermission="delete">
                          <Button
                            variant="outline"
                            size="sm"
                            className="p-2"
                            aria-label="Eliminar reserva"
                            onClick={() => {
                              setReservationIdToDelete(res.id);
                              setIsDeleteModalOpen(true);
                            }}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </RoleBasedRenderer>
                        {res.estado !== "no_show" && res.saldoPendiente > 0 && (
                          <Button 
                            variant="primary" 
                            size="sm" 
                            className="p-2"
                            aria-label="Registrar pago"
                            onClick={() => handleOpenPaymentModal(res)}>
                            <DollarSign size={16} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </ComponentCard>

      {/* Add pagination controls after the table but before modals */}
      {filteredReservas.length > itemsPerPage && (
        <div className="flex items-center justify-between px-4 py-3 sm:px-6 border-t border-gray-200 dark:border-gray-700 mt-4">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md 
                ${currentPage === 1 
                  ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'} 
                border border-gray-300 dark:border-gray-600`}
            >
              Anterior
            </button>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className={`ml-3 relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md
                ${currentPage === totalPages 
                  ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'} 
                border border-gray-300 dark:border-gray-600`}
            >
              Siguiente
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Mostrando <span className="font-medium">{Math.min((currentPage - 1) * itemsPerPage + 1, filteredReservas.length)}</span> a{" "}
                <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredReservas.length)}</span> de{" "}
                <span className="font-medium">{filteredReservas.length}</span> reservas
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 text-sm font-medium
                    ${currentPage === 1 
                      ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                >
                  <span className="sr-only">Anterior</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                
                {/* Page numbers - limited to 5 pages */}
                {(() => {
                  // Calculate which page numbers to show (max 5)
                  let startPage = Math.max(1, currentPage - 2);
                  let endPage = Math.min(totalPages, startPage + 4);
                  
                  // Adjust startPage if we're near the end
                  if (endPage - startPage < 4 && startPage > 1) {
                    startPage = Math.max(1, endPage - 4);
                  }
                  
                  const pages = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium
                          ${currentPage === i
                            ? 'z-10 bg-indigo-50 dark:bg-indigo-900/30 border-indigo-500 dark:border-indigo-500 text-indigo-600 dark:text-indigo-400'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                      >
                        {i}
                      </button>
                    );
                  }
                  return pages;
                })()}
                
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 text-sm font-medium
                    ${currentPage === totalPages 
                      ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                >
                  <span className="sr-only">Siguiente</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Modal de creación/edición */}
      <Modal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        title={editingReserva ? "Editar reserva" : "Nueva reserva"}
      >
        <form onSubmit={handleSaveReserva} className="space-y-4">
          <div className="space-y-4">
            {/* Cliente - Required */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="cliente" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  <User className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
                  Cliente
                  <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setIsNewClientModalOpen(true)}
                  className="text-sm text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 focus:outline-none"
                >
                  + Crear nuevo cliente
                </button>
              </div>
              <select
                id="cliente"
                value={reservaFormData.cliente}
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  setReservaFormData({ ...reservaFormData, cliente: e.target.value })
                }
                className="h-11 w-full appearance-none rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 pr-11 text-sm shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                required
              >
                <option value="">Seleccione un cliente</option>
                {clientes
                  .filter((client) => client.active)
                  .map((client) => (
                    <option key={`${client.id}-${client.name}`} value={client.id.toString()}>
                      {client.name}
                    </option>
                  ))}
              </select>
            </div>

            {/* Excursión - Required */}
            <div className="space-y-2">
              <label htmlFor="excursion" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                <FileText className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
                Excursión
                <span className="text-red-500">*</span>
              </label>
              <select
                id="excursion"
                value={reservaFormData.excursion}
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  setReservaFormData({ ...reservaFormData, excursion: e.target.value })
                }
                className="h-11 w-full appearance-none rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 pr-11 text-sm shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed"
                required
                disabled={!!editingReserva}
              >
                <option value="">Seleccione una excursión</option>
                {excursiones.map((exc) => (
                  <option key={`${exc.id}-${exc.name}`} value={exc.name}>
                    {exc.name}
                  </option>
                ))}
              </select>
              {editingReserva && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No es posible cambiar la excursión una vez creada la reserva
                </p>
              )}
            </div>

            {/* Fecha - Required */}
            <div className="space-y-2">
              <label htmlFor="fecha" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                <Calendar className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
                Fecha
                <span className="text-red-500">*</span>
              </label>
              <Input
                id="fecha"
                type="date"
                name="fecha"
                value={reservaFormData.fecha}
                onChange={(e) => setReservaFormData({ ...reservaFormData, fecha: e.target.value })}
                className="w-full"
                placeholder="Seleccionar fecha"
                autoComplete="off"
              />
            </div>

            {/* Adultos y Niños */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="adultos" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  <Users className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
                  Adultos
                  <span className="text-red-500">*</span>
                </label>
                <Input
                  id="adultos"
                  type="number"
                  name="adultos"
                  value={reservaFormData.adultos}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setReservaFormData({ ...reservaFormData, adultos: e.target.value })
                  }
                  min="1"
                  placeholder="1"
                  className="w-full"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="ninios" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  <User className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
                  Niños
                </label>
                <Input
                  id="ninios"
                  type="number"
                  name="ninios"
                  value={reservaFormData.ninios}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setReservaFormData({ ...reservaFormData, ninios: e.target.value })
                  }
                  min="0"
                  placeholder="0"
                  className="w-full"
                />
              </div>
            </div>

            {/* Seguro de viaje (checkbox) */}
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="incluyeSeguro"
                  type="checkbox"
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  checked={reservaFormData.incluyeSeguro}
                  onChange={(e) =>
                    setReservaFormData({ ...reservaFormData, incluyeSeguro: e.target.checked })
                  }
                />
                <label htmlFor="incluyeSeguro" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Incluye seguro de viaje ($5 por persona)
                </label>
                {reservaFormData.incluyeSeguro && calculatedPricePreview && (
                  <span className="ml-2 text-xs text-green-600 dark:text-green-400">
                    +${(5 * (parseInt(reservaFormData.adultos) + parseInt(reservaFormData.ninios || "0"))).toFixed(2)}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Agrega $5 por persona (adultos y niños) al costo total
              </p>
            </div>

            {/* Hotel - Required */}
            <div className="space-y-2">
              <label htmlFor="hotel" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                <Building2 className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
                Hotel
                <span className="text-red-500">*</span>
              </label>
              <select
                id="hotel"
                value={reservaFormData.hotel}
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  setReservaFormData({ ...reservaFormData, hotel: e.target.value })
                }
                className="h-11 w-full appearance-none rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 pr-11 text-sm shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                required
              >
                <option value="">Seleccione un hotel</option>
                {hotels.map((hotel, index) => (
                  <option key={index} value={hotel}>
                    {hotel}
                  </option>
                ))}
              </select>
            </div>

            {/* Duplicate check warning component */}
            {!editingReserva && reservaFormData.cliente && reservaFormData.fecha && (
              <DuplicateReservationCheck
                name={clientes.find(c => c.id.toString() === reservaFormData.cliente)?.name || ""}
                date={reservaFormData.fecha}
                hotel={reservaFormData.hotel}
                adultos={reservaFormData.adultos ? parseInt(reservaFormData.adultos) : undefined}
                ninios={reservaFormData.ninios ? parseInt(reservaFormData.ninios) : undefined}
                type="b2c"
                onShowDetails={(id) => {
                  // Close current modal, and open the details modal for the selected reservation
                  setIsFormModalOpen(false);
                  // Find the reservation in the existing reservations
                  const reservation = reservas.find(r => r.id === id);
                  if (reservation) {
                    setSelectedReserva(reservation);
                    setIsDetailModalOpen(true);
                  } else {
                    // If not found locally, you might need to fetch it
                    toast.error("No se pudo encontrar la reserva seleccionada");
                  }
                }}
              />
            )}

            {/* Método de pago */}
            <div className="space-y-2">
              <label htmlFor="metodoPago" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                <CreditCard className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
                Método de pago
              </label>
              <select
                id="metodoPago"
                value={reservaFormData.metodoPago}
                onChange={(e) => setReservaFormData(prev => ({
                  ...prev,
                  metodoPago: e.target.value as "efectivo" | "tarjeta" | "transferencia"
                }))}
                className="h-11 w-full appearance-none rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 pr-11 text-sm shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
              >
                <option value="efectivo">Efectivo</option>
                <option value="tarjeta">Tarjeta</option>
                <option value="transferencia">Transferencia</option>
              </select>
            </div>

            {/* Monto facturado */}
            <div className="space-y-2">
              <label htmlFor="montoFacturado" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                <DollarSign className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
                Monto facturado
                <span className="text-red-500">*</span>
              </label>
              <Input
                id="montoFacturado"
                type="number"
                name="montoFacturado"
                value={reservaFormData.montoFacturado}
                onChange={(e) => setReservaFormData({ ...reservaFormData, montoFacturado: e.target.value })}
                disabled={!!editingReserva}
                placeholder="0.00"
                step="0.01"
                min="0"
                className="w-full"
              />
              {editingReserva && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Para pagos adicionales, use la opción "Registrar pago" desde la lista de reservas
                </p>
              )}
            </div>

            {/* Justificación de sobrepago - mostrar solo cuando montoFacturado > calculatedPricePreview */}
            {!editingReserva && calculatedPricePreview !== null && 
              parseFloat(reservaFormData.montoFacturado || "0") > calculatedPricePreview && (
              <div className="space-y-2">
                <label htmlFor="justificacionSobrepago" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  <AlertCircle className="w-4 h-4 inline-block mr-1.5 text-warning-500" />
                  Justificación de sobrepago
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="justificacionSobrepago"
                  name="justificacionSobrepago"
                  value={justificacionSobrepago}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setJustificacionSobrepago(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md border-gray-300 focus:ring-warning-500 focus:border-warning-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  rows={3}
                  placeholder="Explique por qué el pago inicial excede el precio total de la reserva"
                  required
                />
                <p className="text-sm text-warning-500 dark:text-warning-400">
                  El monto facturado (${parseFloat(reservaFormData.montoFacturado || "0").toFixed(2)}) excede el precio total calculado (${calculatedPricePreview.toFixed(2)}) por ${(parseFloat(reservaFormData.montoFacturado || "0") - calculatedPricePreview).toFixed(2)}. Se requiere una justificación para este sobrepago.
                </p>
              </div>
            )}

            {/* Notas adicionales */}
            <div className="space-y-2">
              <label htmlFor="notas" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                <ClipboardList className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
                Notas adicionales
              </label>
              <textarea
                id="notas"
                name="notas"
                value={reservaFormData.notas}
                onChange={(e) => setReservaFormData({ ...reservaFormData, notas: e.target.value })}
                className="w-full px-3 py-2 border rounded-md border-gray-300 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                rows={3}
                placeholder="Detalles adicionales sobre la reserva"
              />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Incluya cualquier información adicional relevante para esta reserva
              </p>
            </div>

            {/* Descuento e Impuestos */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="descuento" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  <TrendingUp className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
                  Descuento (%)
                </label>
                <Input
                  id="descuento"
                  type="number"
                  name="descuento"
                  value={reservaFormData.descuento}
                  onChange={(e) => setReservaFormData({ ...reservaFormData, descuento: e.target.value })}
                  min="0"
                  max="100"
                  placeholder="0"
                  className="w-full"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="taxRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  <Tag className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
                  Impuestos (%)
                </label>
                <Input
                  id="taxRate"
                  type="number"
                  name="taxRate"
                  value={reservaFormData.taxRate}
                  onChange={(e) => setReservaFormData({ ...reservaFormData, taxRate: e.target.value })}
                  min="0"
                  max="100"
                  placeholder="0"
                  className="w-full"
                />
              </div>
            </div>

            {/* Estado especial (solo en modo edición) */}
            {editingReserva && (
              <div className="space-y-2 mt-2">
                <label htmlFor="estado" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  <FileCheck className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
                  Estado especial
                </label>
                <select
                  id="estado"
                  value={reservaFormData.estado}
                  onChange={(e) => setReservaFormData(prev => ({
                    ...prev,
                    estado: e.target.value
                  }))}
                  className="h-11 w-full appearance-none rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 pr-11 text-sm shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                >
                  <option value="">Mantener estado actual</option>
                  <option 
                    value="no_show"
                    disabled={editingReserva?.estado === "completed"}
                  >
                    No Show {editingReserva?.estado === "completed" ? '(No disponible para reservas completadas)' : ''}
                  </option>
                  <option 
                    value="completed" 
                    disabled={editingReserva?.saldoPendiente > 0}
                  >
                    Completada {editingReserva?.saldoPendiente > 0 ? '(Requiere pago total)' : ''}
                  </option>
                </select>
                
                {editingReserva?.estado === "completed" ? (
                  <div className="flex items-center mt-1.5 p-2 bg-gray-50 dark:bg-gray-800/50 rounded-md border border-gray-200 dark:border-gray-700">
                    <Info className="w-4 h-4 text-primary-500 mr-2 flex-shrink-0" />
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Las reservas completadas no pueden marcarse como No-Show.
                    </p>
                  </div>
                ) : editingReserva?.saldoPendiente > 0 ? (
                  <div className="flex items-center mt-1.5 p-2 bg-gray-50 dark:bg-gray-800/50 rounded-md border border-gray-200 dark:border-gray-700">
                    <AlertCircle className="w-4 h-4 text-warning-500 mr-2 flex-shrink-0" />
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Saldo pendiente: ${editingReserva.saldoPendiente.toFixed(2)}. 
                      La reserva debe estar totalmente pagada para marcarla como Completada.
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1.5">
                    Solo seleccionar si desea marcar la reserva como No Show o Completada.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Price preview */}
          {isFormModalOpen && reservaFormData.excursion && (
            <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Precio total estimado:
                </span>
                <span className="text-lg font-bold text-primary-600 dark:text-white">
                  {isCalculatingPrice ? (
                    <span className="text-sm text-gray-500 dark:text-gray-400">Calculando...</span>
                  ) : calculatedPricePreview !== null ? (
                    `$${calculatedPricePreview.toFixed(2)}`
                  ) : (
                    <span className="text-sm text-gray-500 dark:text-gray-400">Seleccione excursión y personas</span>
                  )}
                </span>
              </div>
              {calculatedPricePreview !== null && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Precio con {reservaFormData.descuento || "0"}% de descuento y {reservaFormData.taxRate || "0"}% de impuestos
                </p>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-gray-200 dark:border-gray-700">
            <Button variant="outline" type="button" onClick={() => setIsFormModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              {editingReserva ? "Actualizar reserva" : "Crear reserva"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal para confirmar eliminación */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setReservationIdToDelete(null);
        }}
        title="Confirmación de eliminación"
        size="md"
      >
        <div className="space-y-6 px-2">
          <div className="space-y-4">
            <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
              ¿Está seguro de eliminar esta reserva?
            </p>
            
            <div className="space-y-3">
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                Esta acción eliminará permanentemente todos los datos asociados a la reserva, incluyendo:
              </p>
              
              <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 ml-4 space-y-1">
                <li>Detalles de la reserva y excursión</li>
                <li>Registro de pagos realizados</li>
                <li>Historial de cambios y notas</li>
                <li>Cualquier otro dato relacionado</li>
              </ul>
            </div>

            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 rounded-lg">
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                Esta acción no se puede deshacer.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setReservationIdToDelete(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={async () => {
                if (reservationIdToDelete !== null) {
                  try {
                    await onDeleteReserva(reservationIdToDelete);
                    toast.success("Reserva eliminada exitosamente");
                    setIsDeleteModalOpen(false);
                    setReservationIdToDelete(null);
                  } catch (error) {
                    toast.error("Error al eliminar la reserva");
                  }
                }
              }}
            >
              Sí, eliminar reserva
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal para registrar pago adicional */}
      <Modal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        title="Registrar pago adicional"
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="paymentAmount" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Monto del pago
            </label>
            <input
              id="paymentAmount"
              type="number"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-brand-500 focus:border-brand-500"
              required
            />
            {selectedReservaForPayment && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Saldo pendiente: ${selectedReservaForPayment.saldoPendiente.toFixed(2)}
              </p>
            )}
          </div>
          
          <div>
            <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Método de pago
            </label>
            <select
              id="paymentMethod"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value as "efectivo" | "tarjeta" | "transferencia")}
              className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="efectivo">Efectivo</option>
              <option value="tarjeta">Tarjeta</option>
              <option value="transferencia">Transferencia</option>
            </select>
          </div>
          
          {/* Conditional rendering of justification field */}
          {Number(paymentAmount) > (selectedReservaForPayment?.saldoPendiente || 0) && (
            <div>
              <label htmlFor="justificacionExtra" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Justificación del pago extra <span className="text-red-500">*</span>
              </label>
              <textarea
                id="justificacionExtra"
                value={justificacionExtra}
                onChange={(e) => setJustificacionExtra(e.target.value)}
                className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                placeholder="Indique la razón del pago adicional al saldo pendiente"
                rows={3}
                required
              />
              <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                El monto excede el saldo pendiente por ${(Number(paymentAmount) - (selectedReservaForPayment?.saldoPendiente || 0)).toFixed(2)}. Se requiere justificación.
              </p>
            </div>
          )}
          
          <div className="flex justify-end space-x-2">
            <Button variant="outline" type="button" onClick={() => setIsPaymentModalOpen(false)}>
              Cancelar
            </Button>
            <Button 
              variant="primary" 
              onClick={handleRegisterPayment}
              disabled={Number(paymentAmount) > (selectedReservaForPayment?.saldoPendiente || 0) && !justificacionExtra.trim()}
            >
              Confirmar pago
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de detalles */}
      {isDetailModalOpen && selectedReserva && (
        <ReservaDetailsModal
          isOpen={isDetailModalOpen}
          onClose={() => setIsDetailModalOpen(false)}
          reserva={selectedReserva}
          type="b2c"
          noShows={noShows}
          userRole={userRole}
          onRegisterNoShow={handleRegisterNoShow}
          onDeleteNoShow={handleDeleteNoShow}
          onNoShowChange={refreshNoShows}
        />
      )}

      {/* Modal para crear nuevo cliente */}
      <CreateClientModal
        isOpen={isNewClientModalOpen}
        onClose={() => setIsNewClientModalOpen(false)}
        onAddClient={createClient}
        onSuccess={(newClient) => {
          setClientes((prev) => [...prev, newClient]);
          setReservaFormData((prev) => ({
            ...prev,
            cliente: newClient.id.toString(),
          }));
        }}
      />
    </div>
  );
}

function getStatusBadgeColor(status: string): BadgeColor {
  switch (status) {
    case "pending_payment":
      return "primary";
    case "partial_payment":
      return "info";
    case "paid":
      return "success";
    case "no_show":
      return "warning";
    case "completed":
      return "dark";
    default:
      return "light";
  }
}